// API 服务模块 - 封装与后端的交互

import CONFIG from '../config.js';

class ApiService {
  constructor() {
    this.baseURL = CONFIG.getApiBaseUrl();
    const envInfo = CONFIG.getEnvironmentInfo();

    console.log(`🚀 API服务初始化 - 环境: ${envInfo.environment}, 基础URL: ${this.baseURL}`);
  }

  // 获取当前最新的token
  getCurrentToken() {
    return localStorage.getItem('authToken') || null;
  }

  // 通用请求方法
  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    // 添加认证token（如果需要）- 每次都获取最新的token
    const currentToken = this.getCurrentToken();
    if (currentToken && config.requiresAuth !== false) {
      config.headers.Authorization = `Bearer ${currentToken}`;
      console.log(`🔐 添加认证头: Bearer ${currentToken.substring(0, 20)}...`);
    }

    try {
      console.log(`🚀 API请求: ${config.method || 'GET'} ${url}`);
      const response = await fetch(url, config);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const data = await response.json();
      console.log(`✅ API响应: ${endpoint}`, data);
      return data;
    } catch (error) {
      console.error(`❌ API错误: ${endpoint}`, error);
      throw error;
    }
  }

  // GET 请求
  async get(endpoint, options = {}) {
    return this.request(endpoint, { ...options, method: 'GET' });
  }

  // POST 请求
  async post(endpoint, data, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // PUT 请求
  async put(endpoint, data, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // DELETE 请求
  async delete(endpoint, options = {}) {
    return this.request(endpoint, { ...options, method: 'DELETE' });
  }

  // ========== 记录相关 API ==========

  // 创建音频记录
  async createRecord(audioId, durationSeconds = 0) {
    return this.post('/api/records', {
      audioId,
      durationSeconds,
    });
  }

  // 获取记录列表
  async getRecords(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.get(`/api/records${query ? `?${query}` : ''}`);
  }

  // 获取单个记录详情
  async getRecord(audioId) {
    return this.get(`/api/records/${audioId}`);
  }

  // 更新记录
  async updateRecord(audioId, data) {
    return this.put(`/api/records/${audioId}`, data);
  }

  // 删除记录
  async deleteRecord(audioId) {
    return this.delete(`/api/records/${audioId}`);
  }

  // 获取上一条记录
  async getPreviousRecord(audioId, isFav = false) {
    const query = isFav ? '?is_fav=true' : '';
    return this.get(`/api/records/${audioId}/prev${query}`);
  }

  // 获取下一条记录
  async getNextRecord(audioId, isFav = false) {
    const query = isFav ? '?is_fav=true' : '';
    return this.get(`/api/records/${audioId}/next${query}`);
  }

  // ========== 转写相关 API ==========

  // Qwen3 V1 转写（适合短音频）
  async transcribeV1(audioData, audioId) {
    const formData = new FormData();
    formData.append('audio', audioData, `${audioId}.wav`);
    formData.append('audioId', audioId);

    return this.request('/api/qwen3v1-transcribe', {
      method: 'POST',
      body: formData,
      headers: {}, // 让浏览器自动设置Content-Type
    });
  }

  // Qwen3 V2 转写（支持长音频）
  async transcribeV2(audioData, audioId) {
    // 将音频数据转换为base64
    const audioBase64 = await this.blobToBase64(audioData);

    return this.post('/api/qwen3v2-transcribe', {
      audio: `data:audio/wav;base64,${audioBase64}`,
      audioId: audioId,
      format: 'wav'
    });
  }

  // ========== 润色相关 API ==========

  // 文本润色
  async polishText(text, instruction) {
    return this.post('/api/polish', {
      text,
      instruction,
    }, { requiresAuth: true }); // 润色API需要认证以获取用户设置
  }

  // ========== 工具方法 ==========

  // 生成唯一ID
  generateAudioId() {
    const date = new Date();
    const timestamp = date.getFullYear() +
      (date.getMonth() + 1).toString().padStart(2, '0') +
      date.getDate().toString().padStart(2, '0') + '-' +
      date.getHours().toString().padStart(2, '0') +
      date.getMinutes().toString().padStart(2, '0') +
      date.getSeconds().toString().padStart(2, '0');
    return `audio_${timestamp}_${Math.random().toString(36).substr(2, 9)}`;
  }

  // 格式化时间
  formatDuration(seconds) {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    } else {
      return `${minutes}:${secs.toString().padStart(2, '0')}`;
    }
  }

  // 格式化文件大小
  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  // 将Blob转换为Base64
  async blobToBase64(blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  // 估算音频时长
  async estimateAudioLength(blob) {
    if (!blob) return null;

    try {
      const fileSizeKB = blob.size / 1024;
      const mimeType = blob.type;

      // 基于文件大小的粗略估算
      let bitrate = 128; // 默认比特率 (kbps)

      if (mimeType.includes('webm')) {
        bitrate = 64; // WebM 通常压缩率更高
      } else if (mimeType.includes('mp3')) {
        bitrate = 128;
      } else if (mimeType.includes('wav')) {
        bitrate = 1411; // WAV 无损
      }

      const estimatedSeconds = (fileSizeKB * 8) / bitrate;
      return Math.round(estimatedSeconds);
    } catch (error) {
      console.error('估算音频时长失败:', error);
      return null;
    }
  }

  // 获取音频格式
  getAudioFormat(blob, file = null) {
    if (file && file.type) {
      return file.type.split('/')[1] || 'webm';
    }
    if (blob && blob.type) {
      return blob.type.split('/')[1] || 'webm';
    }
    return 'webm'; // 默认格式
  }

  // 下载音频文件
  async downloadAudio(audioId) {
    const url = `${this.baseURL}/api/audio/${audioId}/download`;

    try {
      console.log(`🚀 开始下载音频文件: ${audioId}`);

      // 获取认证token
      const token = localStorage.getItem('authToken');
      const headers = {};

      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
        console.log(`🔐 添加认证头到音频下载请求`);
      } else {
        console.log(`⚠️ 没有找到认证token，音频下载可能失败`);
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: headers
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      // 获取文件名
      const contentDisposition = response.headers.get('content-disposition');
      let fileName = `audio_${audioId}.wav`; // 默认扩展名
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename="([^"]+)"/);
        if (fileNameMatch) {
          fileName = fileNameMatch[1];
        }
      }

      // 确保文件名有扩展名
      if (!fileName.includes('.')) {
        // 根据Content-Type推断扩展名
        const contentType = response.headers.get('content-type');
        if (contentType) {
          if (contentType.includes('wav')) {
            fileName += '.wav';
          } else if (contentType.includes('mp3')) {
            fileName += '.mp3';
          } else if (contentType.includes('webm')) {
            fileName += '.webm';
          } else if (contentType.includes('ogg')) {
            fileName += '.ogg';
          } else {
            fileName += '.wav'; // 默认扩展名
          }
        }
      }

      // 创建blob
      const blob = await response.blob();

      // 创建下载链接
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      console.log(`✅ 音频文件下载完成: ${fileName}`);
      return { success: true, fileName };
    } catch (error) {
      console.error(`❌ 音频文件下载失败: ${error}`);
      throw error;
    }
  }

  // 健康检查
  async healthCheck() {
    return this.get('/api/health', { requiresAuth: false });
  }

  // 检查音频文件是否存在
  async checkAudioFileExists(audioId) {
    return this.get(`/api/audio/${audioId}/exists`, { requiresAuth: true });
  }

  // 获取音频文件用于播放
  async getAudioFile(audioId) {
    const url = `${this.baseURL}/api/audio/${audioId}/download`;

    try {
      console.log(`🚀 开始获取音频文件: ${audioId}`);

      // 获取认证token
      const token = localStorage.getItem('authToken');
      const headers = {};

      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
        console.log(`🔐 添加认证头到音频下载请求`);
      } else {
        console.log(`⚠️ 没有找到认证token，音频下载可能失败`);
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: headers
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      // 获取文件名
      const contentDisposition = response.headers.get('content-disposition');
      let fileName = `audio_${audioId}.wav`; // 默认扩展名
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename="([^"]+)"/);
        if (fileNameMatch) {
          fileName = fileNameMatch[1];
        }
      }

      // 获取MIME类型
      const contentType = response.headers.get('content-type') || 'audio/wav';

      // 创建blob
      const blob = await response.blob();

      // 创建对象URL用于播放
      const audioUrl = URL.createObjectURL(blob);

      console.log(`✅ 音频文件获取完成: ${fileName}`);
      return {
        success: true,
        audioUrl,
        blob,
        fileName,
        contentType
      };
    } catch (error) {
      console.error(`❌ 音频文件获取失败: ${error}`);
      throw error;
    }
  }

  // ========== 用户相关 API ==========

  // Google OAuth 登录
  async googleOAuthLogin(token) {
    return this.post('/api/auth/oauth/google', {
      token: token
    }, { requiresAuth: false });
  }

  // 获取当前用户信息
  async getCurrentUser() {
    return this.get('/api/auth/me');
  }

  // 用户登出
  async logout() {
    return this.post('/api/auth/logout', {}, { requiresAuth: true });
  }

  // 刷新Token
  async refreshToken(refreshToken) {
    return this.post('/api/auth/refresh', {
      refreshToken: refreshToken
    }, { requiresAuth: false });
  }

  // ========== 设置相关 API ==========

  // 获取用户设置
  async getUserSettings() {
    return this.get('/api/settings');
  }

  // 保存用户设置
  async saveUserSettings(settings) {
    return this.put('/api/settings', settings);
  }

  // 清空用户设置
  async clearUserSettings() {
    return this.delete('/api/settings');
  }

  // 获取默认设置
  async getDefaultSettings() {
    return this.get('/api/settings/default');
  }

  // ========== 文件上传下载 API ==========

  // 文件上传（CloudFlare R2）
  async uploadFile(file, fileId) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('fileId', fileId);

    return this.request('/api/upload', {
      method: 'POST',
      body: formData,
      headers: {}, // 让浏览器自动设置Content-Type
      requiresAuth: false, // 上传接口不需要认证
    });
  }

  // 文件下载（CloudFlare R2）
  async downloadFile(fileId) {
    const url = `${this.baseURL}/api/download/${fileId}`;

    try {
      console.log(`🚀 开始下载文件: ${fileId}`);

      const response = await fetch(url, {
        method: 'GET',
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      // 获取文件名
      const contentDisposition = response.headers.get('content-disposition');
      let fileName = `download`; // 默认文件名
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename="([^"]+)"/);
        if (fileNameMatch) {
          fileName = decodeURIComponent(fileNameMatch[1]);
        }
      }

      // 创建blob
      const blob = await response.blob();

      // 创建下载链接
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      console.log(`✅ 文件下载完成: ${fileName}`);
      return { success: true, fileName };
    } catch (error) {
      console.error(`❌ 文件下载失败: ${error}`);
      throw error;
    }
  }

  // 设置认证token（用于登录功能）
  setToken(token) {
    if (token) {
      localStorage.setItem('authToken', token);
      console.log(`🔐 设置新的认证token: ${token.substring(0, 20)}...`);
    } else {
      localStorage.removeItem('authToken');
      console.log(`🔐 清除认证token`);
    }
  }

  // 清除认证token（用于登出功能）
  clearToken() {
    localStorage.removeItem('authToken');
    console.log(`🔐 清除认证token`);
  }
}

// 创建单例实例
const apiService = new ApiService();

export default apiService;